import { lazy, Suspense, useEffect, useState } from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useRouteError,
} from 'react-router'
import { DashboardShell } from '@/components/layout/DashboardShell'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { config } from '@/constants/config'
import { MODULES, permission } from '@/constants/permissions'
import { useLandingPath } from '@/hooks/useLandingPath'
import { usePermission } from '@/hooks/usePermission'
import { HotelPortalRoute } from '@/router/HotelPortalRoute'
import { ProtectedRoute } from '@/router/ProtectedRoute'
import { paths } from '@/router/paths'
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/utils/chunkReload'
import { reportError } from '@/utils/reportError'

/**
 * Thin route config: path -> lazy(screen). No data fetching, no business logic
 * here. Every screen is code-split, so the initial bundle stays small.
 *
 * Module screens are not built yet — they currently render the shared
 * placeholder. Replace each `element` with its real screen as it lands, and
 * keep the surrounding ProtectedRoute permission gate.
 *
 * Built as a **data router** (`createBrowserRouter`) rather than the declarative
 * `<BrowserRouter><Routes>` pair. Nothing here loads data through it — the
 * reason is `useBlocker`, which only a data router provides and which is the
 * only supported way to stop an in-app navigation. Screens with unsaved input
 * (the role wizard) need it to ask before the user loses their work; a click
 * handler on every link could never cover the browser's Back button.
 */
const LoginScreen = lazy(() => import('@/screens/auth/LoginScreen'))
const LoginOtpScreen = lazy(() => import('@/screens/auth/LoginOtpScreen'))
const ForgotPasswordScreen = lazy(() => import('@/screens/auth/ForgotPasswordScreen'))
const ResetPasswordScreen = lazy(() => import('@/screens/auth/ResetPasswordScreen'))
const CustomersListScreen = lazy(() => import('@/screens/customers/CustomersListScreen'))
/** Add and Edit Customer are the same four-step wizard (agent.md §23.38). */
const CustomerWizardScreen = lazy(() => import('@/screens/customers/CustomerWizardScreen'))
const CustomerDetailScreen = lazy(() => import('@/screens/customers/CustomerDetailScreen'))
const HotelsListScreen = lazy(() => import('@/screens/hotels/HotelsListScreen'))
/** Add and Edit Hotel are the same four-step wizard. */
const HotelWizardScreen = lazy(() => import('@/screens/hotels/HotelWizardScreen'))
/** Read-only hotel record: Overview / Events & Groups / Logs tabs. */
const HotelOverviewScreen = lazy(() => import('@/screens/hotels/HotelOverviewScreen'))
const RoomsListScreen = lazy(() => import('@/screens/rooms/RoomsListScreen'))
/** Add and Edit Room Inventory are the same three-step wizard. */
const RoomInventoryWizardScreen = lazy(() => import('@/screens/rooms/RoomInventoryWizardScreen'))
const RoomTypeOverviewScreen = lazy(() => import('@/screens/rooms/RoomTypeOverviewScreen'))
const ReservationsListScreen = lazy(() => import('@/screens/reservations/ReservationsListScreen'))
const ReservationWizardScreen = lazy(() => import('@/screens/reservations/ReservationWizardScreen'))
const ReservationDetailScreen = lazy(() => import('@/screens/reservations/ReservationDetailScreen'))
const WaitlistListScreen = lazy(() => import('@/screens/waitlist/WaitlistListScreen'))
/** Add Waitlist — a three-step wizard (Request / Stay / Review). */
const WaitlistWizardScreen = lazy(() => import('@/screens/waitlist/WaitlistWizardScreen'))
/** One request: Overview / Logs tabs, plus the Edit drawer and action dialogs. */
const WaitlistDetailScreen = lazy(() => import('@/screens/waitlist/WaitlistDetailScreen'))
const SecureLinksListScreen = lazy(() => import('@/screens/secure-links/SecureLinksListScreen'))
/** Create New Batch — event, hotel, Ready to Send reservations, then the Send modal. */
const SecureLinkCreateScreen = lazy(() => import('@/screens/secure-links/SecureLinkCreateScreen'))
/** One batch; Overview, Reservations, Send History and Activity are each a route. */
const SecureLinkDetailScreen = lazy(() => import('@/screens/secure-links/SecureLinkDetailScreen'))
const EventsListScreen = lazy(() => import('@/screens/events/EventsListScreen'))
/** Add and Edit Event are the same five-step wizard. */
const EventWizardScreen = lazy(() => import('@/screens/events/EventWizardScreen'))
/** Read-only event record: Overview / Hotels / Logs tabs. */
const EventOverviewScreen = lazy(() => import('@/screens/events/EventOverviewScreen'))
/** User Management > Users. Create and Edit are one modal over this listing. */
const UsersListScreen = lazy(() => import('@/screens/users/UsersListScreen'))
/** User Management > Roles. Create and Edit are the same three-step wizard. */
const RolesListScreen = lazy(() => import('@/screens/roles/RolesListScreen'))
const RoleWizardScreen = lazy(() => import('@/screens/roles/RoleWizardScreen'))
/** Website Content. Create and Edit are one screen; `?type=` picks the form. */
const WebsiteContentListScreen = lazy(() => import('@/screens/cms/WebsiteContentListScreen'))
const PageEditorScreen = lazy(() => import('@/screens/cms/PageEditorScreen'))
const PagePreviewScreen = lazy(() => import('@/screens/cms/PagePreviewScreen'))
/*
 * LEGACY user screens, superseded by the two above (2026-09-15). Nothing links
 * to them; they stay routed so a typed URL still works — see
 * `src/screens/users/agent.md` §23.1.
 */
const UserFormScreen = lazy(() => import('@/screens/users/UserFormScreen'))
const RolesScreen = lazy(() => import('@/screens/users/RolesScreen'))
const RolePermissionsScreen = lazy(() => import('@/screens/users/RolePermissionsScreen'))
/**
 * The signed-in user's own account, from the topbar's account menu. Gated by
 * nothing but a session — see the route below.
 */
const UserProfileScreen = lazy(() => import('@/screens/profile/UserProfileScreen'))
/** Global Configuration — superadmin only (settings/agent.md §23.2). */
const SettingsLandingScreen = lazy(() => import('@/screens/settings/SettingsLandingScreen'))
const AmenitiesListScreen = lazy(() => import('@/screens/settings/AmenitiesListScreen'))
const AdditionalTypesListScreen = lazy(() => import('@/screens/settings/AdditionalTypesListScreen'))
const CountriesListScreen = lazy(() => import('@/screens/settings/CountriesListScreen'))
const RegionsListScreen = lazy(() => import('@/screens/settings/RegionsListScreen'))
const CurrenciesListScreen = lazy(() => import('@/screens/settings/CurrenciesListScreen'))
const EmailTemplatesListScreen = lazy(() => import('@/screens/settings/EmailTemplatesListScreen'))
const EmailTemplateEditScreen = lazy(() => import('@/screens/settings/EmailTemplateEditScreen'))
const SystemMessagesListScreen = lazy(() => import('@/screens/settings/SystemMessagesListScreen'))
const OfficeHoursScreen = lazy(() => import('@/screens/settings/OfficeHoursScreen'))
/** Reports — the report picker and every report (screens/reports/agent.md). */
const ReportsScreen = lazy(() => import('@/screens/reports/ReportsScreen'))
/**
 * The hotel-facing Secure Hotel Portal — a different audience with its own
 * session, outside the staff shell (screens/hotel-portal/agent.md).
 */
const HotelPortalAccessScreen = lazy(() => import('@/screens/hotel-portal/HotelPortalAccessScreen'))
const HotelPortalReservationsScreen = lazy(
  () => import('@/screens/hotel-portal/HotelPortalReservationsScreen'),
)
const ForbiddenScreen = lazy(() => import('@/screens/errors/ForbiddenScreen'))
const NotFoundScreen = lazy(() => import('@/screens/errors/NotFoundScreen'))
const Placeholder = lazy(() => import('@/screens/ModulePlaceholderScreen'))
const DashboardScreen = lazy(() => import('@/screens/dashboard/DashboardScreen'))

/**
 * The app's one Suspense boundary. Every screen is `React.lazy`, so the whole
 * route tree sits inside a pathless layout route that supplies the fallback —
 * `RouterProvider` takes no children, so it cannot be wrapped from outside.
 */
/**
 * A data router catches render errors itself, *above* the app-level
 * `ErrorBoundary` in `App.tsx` — so without this the portal would fall back to
 * React Router's own default error page. This keeps the behaviour CLAUDE.md §15
 * describes: the same neutral recovery UI, and the error still reported.
 *
 * Most screen crashes never get here: `DashboardShell` wraps its outlet in an
 * `ErrorBoundary` of its own, so the shell survives one broken screen. This is
 * the outer net — the shell itself, and the routes outside it.
 */
function RouteErrorBoundary() {
  const error = useRouteError()
  const [reloadingStaleChunk, setReloadingStaleChunk] = useState(() => isChunkLoadError(error))

  useEffect(() => {
    // A stale screen chunk is recovered by a reload, not reported.
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) return
    setReloadingStaleChunk(false)
    reportError(error, { scope: 'route' })
  }, [error])

  if (reloadingStaleChunk) {
    return (
      <div className="p-6">
        <Skeleton variant="form" rows={3} label="Loading" />
      </div>
    )
  }

  return (
    <EmptyState
      className="min-h-svh"
      title="Something Went Wrong"
      description={
        config.isDev && error instanceof Error
          ? error.message
          : 'The screen failed to load. Please try again.'
      }
      action={<Button onClick={() => window.location.reload()}>Reload</Button>}
    />
  )
}

function SuspenseLayout() {
  return (
    <Suspense
      fallback={
        <div className="p-6">
          <Skeleton variant="form" rows={3} label="Loading" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  )
}

/**
 * System > User Management has no screen of its own, so its row lands here.
 *
 * Which child it opens depends on the grant: the two carry DIFFERENT
 * permissions, and a role holding only `role.view` would be bounced to
 * /403 by a fixed redirect to Users. This is a permission gate, which is the
 * one kind of conditional the router is allowed to hold (CLAUDE.md §8).
 */
function UserManagementIndex() {
  const { canModule } = usePermission()

  return (
    <Navigate
      to={
        canModule(MODULES.users, 'view') ? paths.userManagement.users : paths.userManagement.roles
      }
      replace
    />
  )
}

/**
 * The portal's home (`/`).
 *
 * `dashboard.view` is a grant like any other, so a role can be without it. Such
 * a user is not sent to /403 for opening the app: they land on the FIRST screen
 * their role can open, which is also the first row of their own sidebar — see
 * `useLandingPath`. Every sign-in, the brand logo and both error screens point
 * at `/`, so putting the decision here covers all of them.
 *
 * A role that can open nothing at all still gets /403: that is a misconfigured
 * role, and inventing a screen for it would only hide the problem.
 *
 * This is a permission gate, the one kind of conditional the router may hold
 * (CLAUDE.md §8).
 */
function HomeIndex() {
  const { canViewModule } = usePermission()
  const landing = useLandingPath()

  if (canViewModule(MODULES.dashboard)) return <DashboardScreen />

  return <Navigate to={landing ?? paths.forbidden} replace />
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<SuspenseLayout />} errorElement={<RouteErrorBoundary />}>
      <Route path={paths.login} element={<LoginScreen />} />
      <Route path={paths.loginVerifyOtp} element={<LoginOtpScreen />} />
      <Route path={paths.forgotPassword} element={<ForgotPasswordScreen />} />
      <Route path={paths.resetPassword} element={<ResetPasswordScreen />} />

      {/* The hotel-facing portal: the emailed link, then the hotel's
          reservations. Gated by the portal session, never the staff one. */}
      <Route path={paths.hotelPortal.access(':token')} element={<HotelPortalAccessScreen />} />
      <Route
        path={paths.hotelPortal.reservations}
        element={
          <HotelPortalRoute>
            <HotelPortalReservationsScreen />
          </HotelPortalRoute>
        }
      />

      {/* A Website Content page's full-tab preview, opened from the editor. It
          sits outside the shell on purpose: the point is the whole window. */}
      <Route
        path={paths.cms.preview(':pageKey')}
        element={
          <ProtectedRoute permission={permission(MODULES.cms, 'view')}>
            <PagePreviewScreen />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <DashboardShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeIndex />} />

        <Route
          path={paths.customers.list}
          element={
            <ProtectedRoute permission={permission(MODULES.customers, 'view')}>
              <CustomersListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.customers.new}
          element={
            <ProtectedRoute permission={permission(MODULES.customers, 'create')}>
              <CustomerWizardScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.customers.detail(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.customers, 'view')}>
              <CustomerDetailScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.customers.edit(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.customers, 'edit')}>
              <CustomerWizardScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path={paths.events.list}
          element={
            <ProtectedRoute permission={permission(MODULES.events, 'view')}>
              <EventsListScreen />
            </ProtectedRoute>
          }
        />
        {/* `new` is declared before `:id` so "/events/new" is never read as an
            event id. */}
        <Route
          path={paths.events.new}
          element={
            <ProtectedRoute permission={permission(MODULES.events, 'create')}>
              <EventWizardScreen />
            </ProtectedRoute>
          }
        />
        {/* The wizard is also the record's read-only view: `event.view` alone
            opens it locked to the Review summary, `event.edit` unlocks it. */}
        <Route
          path={paths.events.edit(':id')}
          element={
            <ProtectedRoute
              anyPermission={[
                permission(MODULES.events, 'edit'),
                permission(MODULES.events, 'view'),
              ]}
            >
              <EventWizardScreen />
            </ProtectedRoute>
          }
        />

        {/* The read-only Event Overview; each tab is a route. */}
        {[
          paths.events.detail(':id'),
          paths.events.hotels(':id'),
          paths.events.photos(':id'),
          paths.events.logs(':id'),
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission(MODULES.events, 'view')}>
                <EventOverviewScreen />
              </ProtectedRoute>
            }
          />
        ))}

        {/* Event preview is not built yet; it lands on the placeholder. */}
        <Route
          path={`${paths.events.list}/*`}
          element={
            <ProtectedRoute permission={permission(MODULES.events, 'view')}>
              <Placeholder
                title="Events"
                summary="Create/edit with schedule, venue, map location picker, hotel price filter, featured flag, contacts repeater, the event fees matrix across the three day bands, statuses, preview, duplication, hotel groups and ACL-gated large event configuration."
              />
            </ProtectedRoute>
          }
        />

        <Route
          path={paths.hotels.list}
          element={
            <ProtectedRoute permission={permission(MODULES.hotels, 'view')}>
              <HotelsListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.hotels.new}
          element={
            <ProtectedRoute permission={permission(MODULES.hotels, 'create')}>
              <HotelWizardScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.hotels.edit(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.hotels, 'edit')}>
              <HotelWizardScreen />
            </ProtectedRoute>
          }
        />
        {/* The read-only Hotel Overview; each tab is a route. */}
        {[
          paths.hotels.detail(':id'),
          paths.hotels.groups(':id'),
          paths.hotels.roomTypes(':id'),
          paths.hotels.photos(':id'),
          paths.hotels.logs(':id'),
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission(MODULES.hotels, 'view')}>
                <HotelOverviewScreen />
              </ProtectedRoute>
            }
          />
        ))}
        {/* Any other hotel path; keeps it from 404ing. */}
        <Route
          path={`${paths.hotels.list}/*`}
          element={
            <ProtectedRoute permission={permission(MODULES.hotels, 'view')}>
              <Placeholder
                title="Hotels"
                summary="Create/edit: location and lat/long, check-in/out times, currency and structured charges, custom information repeater, up-to-7 half-star rating, amenities, images and room type management."
              />
            </ProtectedRoute>
          }
        />

        <Route
          path={paths.rooms.list}
          element={
            <ProtectedRoute permission={permission(MODULES.rooms, 'view')}>
              <RoomsListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.rooms.new}
          element={
            <ProtectedRoute permission={permission(MODULES.rooms, 'create')}>
              <RoomInventoryWizardScreen />
            </ProtectedRoute>
          }
        />
        {/* One room type's page; each tab is a route. */}
        {[
          paths.rooms.roomType(':id'),
          paths.rooms.roomTypeDates(':id'),
          paths.rooms.roomTypeLogs(':id'),
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission(MODULES.rooms, 'view')}>
                <RoomTypeOverviewScreen />
              </ProtectedRoute>
            }
          />
        ))}
        {/* View-gated, not edit-gated: without `room.edit` the record opens as
            a locked summary, the way the other wizards behave. */}
        <Route
          path={paths.rooms.edit(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.rooms, 'view')}>
              <RoomInventoryWizardScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path={paths.reservations.list}
          element={
            <ProtectedRoute permission={permission(MODULES.reservations, 'view')}>
              <ReservationsListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.reservations.new}
          element={
            <ProtectedRoute permission={permission(MODULES.reservations, 'create')}>
              <ReservationWizardScreen />
            </ProtectedRoute>
          }
        />
        {/* The five tabs of one screen; each tab is a route. */}
        {[
          paths.reservations.detail(':id'),
          paths.reservations.stay(':id'),
          paths.reservations.guests(':id'),
          paths.reservations.financials(':id'),
          paths.reservations.activity(':id'),
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission(MODULES.reservations, 'view')}>
                <ReservationDetailScreen />
              </ProtectedRoute>
            }
          />
        ))}

        <Route
          path={paths.waitlist.list}
          element={
            <ProtectedRoute permission={permission(MODULES.waitlist, 'view')}>
              <WaitlistListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.waitlist.new}
          element={
            <ProtectedRoute permission={permission(MODULES.waitlist, 'create')}>
              <WaitlistWizardScreen />
            </ProtectedRoute>
          }
        />
        {/* Overview and Logs are tabs of one screen; each tab is a route. */}
        <Route
          path={paths.waitlist.detail(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.waitlist, 'view')}>
              <WaitlistDetailScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.waitlist.logs(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.waitlist, 'view')}>
              <WaitlistDetailScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path={paths.secureLinks.list}
          element={
            <ProtectedRoute permission={permission(MODULES.secureLinks, 'view')}>
              <SecureLinksListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.secureLinks.new}
          element={
            <ProtectedRoute permission={permission(MODULES.secureLinks, 'create')}>
              <SecureLinkCreateScreen />
            </ProtectedRoute>
          }
        />
        {[
          paths.secureLinks.detail(':id'),
          paths.secureLinks.reservations(':id'),
          paths.secureLinks.history(':id'),
          paths.secureLinks.activity(':id'),
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission(MODULES.secureLinks, 'view')}>
                <SecureLinkDetailScreen />
              </ProtectedRoute>
            }
          />
        ))}

        <Route
          path={`${paths.transactions.list}/*`}
          element={
            <ProtectedRoute permission={permission(MODULES.transactions, 'view')}>
              <Placeholder
                title="Transactions"
                summary="Authorize.Net transaction listing and details. Tokenised payments only — masked payment profiles, never full card numbers or CVV."
              />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${paths.commission.list}/*`}
          element={
            <ProtectedRoute permission={permission(MODULES.commission, 'view')}>
              <Placeholder
                title="Commission"
                summary="Percentage and flat amounts, paid by hotel or client, pay one / pay all settlement and USD conversion at the locked exchange rate."
              />
            </ProtectedRoute>
          }
        />

        {/* Reports — `report.view` (a `report.*` grant covers it). The
            index is the report picker; each report type is a path segment. */}
        {[paths.reports.index, paths.reports.report(':reportType')].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission(MODULES.reports, 'view')}>
                <ReportsScreen />
              </ProtectedRoute>
            }
          />
        ))}

        {/* Roles & Permissions moved to User Management (2026-09-15). These
            redirects keep existing bookmarks working, and sit above the Global
            Settings placeholder below: `/settings/roles` is a static segment,
            so React Router ranks it over `/settings/*`. */}
        <Route
          path={paths.settings.legacyRoles}
          element={<Navigate to={paths.userManagement.roles} replace />}
        />
        <Route
          path={`${paths.settings.legacyRoles}/*`}
          element={<Navigate to={paths.userManagement.roles} replace />}
        />

        {/* Global Configuration — every route superadmin only (client,
            2026-10-01). Any other `/settings/*` URL falls through to 404. */}
        <Route
          path={paths.settings.index}
          element={
            <ProtectedRoute superadminOnly>
              <SettingsLandingScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.amenities}
          element={
            <ProtectedRoute superadminOnly>
              <AmenitiesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.additionalTypes}
          element={
            <ProtectedRoute superadminOnly>
              <AdditionalTypesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.countries}
          element={
            <ProtectedRoute superadminOnly>
              <CountriesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.regions}
          element={
            <ProtectedRoute superadminOnly>
              <RegionsListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.currencies}
          element={
            <ProtectedRoute superadminOnly>
              <CurrenciesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.emailTemplates}
          element={
            <ProtectedRoute superadminOnly>
              <EmailTemplatesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.emailTemplate(':templateId')}
          element={
            <ProtectedRoute superadminOnly>
              <EmailTemplateEditScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.systemMessages}
          element={
            <ProtectedRoute superadminOnly>
              <SystemMessagesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.settings.officeHours}
          element={
            <ProtectedRoute superadminOnly>
              <OfficeHoursScreen />
            </ProtectedRoute>
          }
        />

        {/*
          Website Content. Both routes need `cms.view` — the backend's own grant
          for reading CMS (it has the four CRUD grants only). `cms.edit` gates
          the writes INSIDE the screens: a view-only session still opens the
          editor read-only, because Preview is a view (agent.md §13, §23.3).
        */}
        <Route
          path={paths.cms.list}
          element={
            <ProtectedRoute permission={permission(MODULES.cms, 'view')}>
              <WebsiteContentListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.cms.edit(':pageKey')}
          element={
            <ProtectedRoute permission={permission(MODULES.cms, 'view')}>
              <PageEditorScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${paths.partners.list}/*`}
          element={
            <ProtectedRoute permission={permission(MODULES.partners, 'view')}>
              <Placeholder title="Partners" summary="Manage and publish partner content pages." />
            </ProtectedRoute>
          }
        />

        {/* System > User Management. The container has no screen of its own, so
            it lands on its first child. `new` is a static segment, so React
            Router ranks it above `:roleId` — no ordering hack needed. */}
        <Route path={paths.userManagement.index} element={<UserManagementIndex />} />
        <Route
          path={paths.userManagement.users}
          element={
            <ProtectedRoute permission={permission(MODULES.users, 'view')}>
              <UsersListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.userManagement.roles}
          element={
            <ProtectedRoute permission={permission(MODULES.roles, 'view')}>
              <RolesListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.userManagement.roleNew}
          element={
            <ProtectedRoute permission={permission(MODULES.roles, 'create')}>
              <RoleWizardScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.userManagement.role(':roleId')}
          element={
            <ProtectedRoute permission={permission(MODULES.roles, 'edit')}>
              <RoleWizardScreen />
            </ProtectedRoute>
          }
        />

        {/* LEGACY. The listing moved, so `/users` redirects; the screens below
            it are superseded but still reachable by URL (agent.md §23.1).
            `/users/roles` is a static segment, so it ranks above `/users/:id`. */}
        <Route
          path={paths.users.list}
          element={<Navigate to={paths.userManagement.users} replace />}
        />
        <Route
          path={paths.users.new}
          element={
            <ProtectedRoute permission={permission(MODULES.users, 'create')}>
              <UserFormScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.users.roles}
          element={
            <ProtectedRoute permission={permission(MODULES.roles, 'view')}>
              <RolesScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.users.permissions(':roleId')}
          element={
            <ProtectedRoute permission={permission(MODULES.roles, 'edit')}>
              <RolePermissionsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.users.edit(':id')}
          element={
            <ProtectedRoute permission={permission(MODULES.users, 'edit')}>
              <UserFormScreen />
            </ProtectedRoute>
          }
        />

        {/*
          User Profile. **Inside `ProtectedRoute` but with no permission**, and
          the omission is deliberate: every signed-in user has an account of
          their own, whatever their role grants, and gating this on `users:view`
          would hide the account menu's own row from nearly everybody. The API
          agrees from the other side — its profile endpoints resolve the account
          from the token and accept no id, so there is nothing here to
          authorize. See `src/screens/profile/agent.md` §13.
        */}
        <Route
          path={paths.users.profile}
          element={
            <ProtectedRoute>
              <UserProfileScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path="/audit/*"
          element={
            <ProtectedRoute permission={permission(MODULES.audit, 'view')}>
              <Placeholder title="Audit" summary="API log and audit trail viewers." />
            </ProtectedRoute>
          }
        />

        <Route path={paths.forbidden} element={<ForbiddenScreen />} />
        <Route path="*" element={<NotFoundScreen />} />
      </Route>
    </Route>,
  ),
)
